import { MapPin, AlertCircle, Navigation } from "lucide-react";

/**
 * Renders a Google Maps embed iframe from a URL pasted by the admin.
 * No API key required.
 *
 * Props:
 *   url          — the Google Maps embed URL (e.g. https://www.google.com/maps/embed?pb=...)
 *   height       — iframe height in px (default 280)
 *   label        — accessible title for the iframe
 *   showDirections — show a "Get Directions" button (default true)
 *   directionsHref — the maps URL to open in a new tab (falls back to a search URL)
 *   testid
 */
export default function MapEmbed({
    url,
    height = 280,
    label = "Map",
    showDirections = true,
    directionsHref,
    testid = "map-embed",
}) {
    if (!url) {
        return (
            <div
                data-testid={`${testid}-placeholder`}
                className="w-full bg-slate-900 border border-dashed border-slate-700 rounded flex flex-col items-center justify-center text-center p-6"
                style={{ height }}
            >
                <AlertCircle className="w-8 h-8 text-amber-400 mb-2" />
                <div className="font-display text-sm uppercase tracking-widest text-white">No map set yet</div>
                <p className="text-xs text-slate-400 mt-2 max-w-sm">
                    Admin can paste a Google Maps share-embed URL from `Share → Embed a map`.
                </p>
            </div>
        );
    }
    return (
        <div data-testid={testid}>
            <div className="rounded overflow-hidden border border-slate-700 bg-slate-900" style={{ height }}>
                <iframe
                    src={url}
                    title={label}
                    width="100%"
                    height={height}
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </div>
            {showDirections && directionsHref && (
                <a href={directionsHref} target="_blank" rel="noopener noreferrer"
                    data-testid={`${testid}-directions`}
                    className="mt-3 inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66]/10 border border-[#00FF66]/40 hover:bg-[#00FF66]/20 rounded text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                    <Navigation className="w-3.5 h-3.5" /> Get Directions
                </a>
            )}
        </div>
    );
}

/** Convenience for the most common "open in Google Maps" link from an address. */
export function searchDirectionsHref(address) {
    if (!address) return "https://www.google.com/maps";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/** Extracts the iframe src URL when admin pastes an `<iframe ...>` snippet,
 *  otherwise returns the input as-is. */
export function extractEmbedSrc(raw) {
    if (!raw) return "";
    const s = raw.trim();
    if (s.startsWith("<iframe")) {
        const m = s.match(/src="([^"]+)"/);
        return m ? m[1] : "";
    }
    return s;
}
